import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Blog.css";
import { Helmet } from "react-helmet-async";
import { Icon } from "@iconify/react";
import searchIcon from "@iconify/icons-mdi/magnify";
import closeIcon from "@iconify/icons-mdi/close";
import arrowRightIcon from "@iconify/icons-mdi/arrow-right";
import calendarIcon from "@iconify/icons-mdi/calendar-outline";
import chevronLeft from "@iconify/icons-mdi/chevron-left";
import chevronRight from "@iconify/icons-mdi/chevron-right";
import sparkIcon from "@iconify/icons-mdi/sparkles";
import editorialArticles from "./editorialArticles.json";

const blogsPerPage = 9;

const staticBlogData = Object.entries(editorialArticles).map(([route, article]) => ({
  route,
  title: article.title,
  content: article.description,
  category: article.category,
  author: "",
  date: `Updated ${new Date(`${article.updatedAt}T12:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`,
}));

function BlogCards() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();
  const allBlogs = staticBlogData;

  const categories = useMemo(() => {
    const cats = new Set(["All"]);
    allBlogs.forEach((blog) => cats.add(blog.category));
    return Array.from(cats);
  }, [allBlogs]);

  const filteredBlogs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allBlogs.filter((blog) => {
      const matchesSearch =
        !query ||
        blog.title.toLowerCase().includes(query) ||
        blog.content.toLowerCase().includes(query) ||
        blog.category.toLowerCase().includes(query);
      const matchesCategory =
        selectedCategory === "All" || blog.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allBlogs, searchQuery, selectedCategory]);

  const featuredBlog = useMemo(() => {
    if (searchQuery || selectedCategory !== "All") return null;
    return filteredBlogs[0] || null;
  }, [filteredBlogs, searchQuery, selectedCategory]);

  const gridBlogs = useMemo(() => {
    if (!searchQuery && selectedCategory === "All" && featuredBlog) {
      return filteredBlogs.slice(1);
    }
    return filteredBlogs;
  }, [filteredBlogs, featuredBlog, searchQuery, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(gridBlogs.length / blogsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * blogsPerPage;
  const currentGridBlogs = gridBlogs.slice(startIndex, startIndex + blogsPerPage);

  const handleCardClick = (event, blog) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) return;
    event.preventDefault();
    navigate(blog.route);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 380, behavior: "smooth" });
  };

  const renderCover = (blog, featured = false) => (
    <div
      className={`qr-editorial-cover ${featured ? "qr-editorial-cover-featured" : ""}`}
      data-category={blog.category}
      aria-hidden="true"
    >
      <span className="qr-editorial-cover-kicker">{blog.category}</span>
      <span className="qr-editorial-cover-rule" />
      <span className="qr-editorial-cover-title">{blog.title}</span>
      <span className="qr-editorial-cover-footer">QUAREC RESOURCES · PRACTICAL GUIDE</span>
    </div>
  );

  return (
    <div className="qr-blog-page">
      <Helmet>
        <meta name="author" content="Quarec Resources" />
      </Helmet>

      <section className="qr-blog-hero">
        <div className="qr-hero-badge">
          <Icon icon={sparkIcon} style={{ fontSize: "15px" }} />
          <span>Practical Knowledge</span>
        </div>
        <h1 className="qr-hero-title">Hiring and Career Guides</h1>
        <p className="qr-hero-subtitle">
          Actionable resources for employers and jobseekers: clearer role briefs, more consistent interviews,
          better candidate communication and a more organised job search. These guides provide general
          information; verify current legal and immigration requirements through official sources.
        </p>
      </section>

      <div className="qr-blog-controls">
        <div className="qr-controls-card">
          <div className="qr-search-wrapper">
            <Icon icon={searchIcon} className="qr-search-icon" />
            <input
              type="search"
              aria-label="Search hiring and career guides"
              placeholder="Search guides by title, topic, or keyword..."
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setCurrentPage(1);
              }}
              className="qr-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="qr-search-clear"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                title="Clear search"
                aria-label="Clear search"
              >
                <Icon icon={closeIcon} />
              </button>
            )}
          </div>

          <div className="qr-categories-scroll" aria-label="Filter guides by category">
            {categories.map((category) => (
              <button
                type="button"
                key={category}
                className={`qr-cat-pill ${selectedCategory === category ? "active" : ""}`}
                aria-pressed={selectedCategory === category}
                onClick={() => {
                  setSelectedCategory(category);
                  setCurrentPage(1);
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {featuredBlog && (
        <section className="qr-featured-container" aria-labelledby="qr-featured-heading">
          <a
            className="qr-featured-card"
            href={featuredBlog.route}
            onClick={(event) => handleCardClick(event, featuredBlog)}
          >
            <div className="qr-featured-media">
              {renderCover(featuredBlog, true)}
              <div className="qr-featured-overlay">
                <span className="qr-pill-featured">Featured Guide</span>
              </div>
            </div>
            <div className="qr-featured-body">
              <div className="qr-meta-row">
                <span className="qr-meta-category">{featuredBlog.category}</span>
                <span className="qr-meta-dot" />
                <span>{featuredBlog.date}</span>
              </div>
              <h2 id="qr-featured-heading" className="qr-featured-title">{featuredBlog.title}</h2>
              <p className="qr-featured-excerpt">{featuredBlog.content}</p>
              <div className="qr-featured-footer">
                <div className="qr-read-link">
                  <span>Read guide</span>
                  <Icon icon={arrowRightIcon} />
                </div>
              </div>
            </div>
          </a>
        </section>
      )}

      <section className="qr-grid-container">
        <div className="qr-section-header">
          <h2 className="qr-section-title">
            {searchQuery
              ? `Search results for "${searchQuery}"`
              : selectedCategory !== "All"
                ? `${selectedCategory} guides`
                : "Explore all guides"}
          </h2>
          <span className="qr-section-count">
            {filteredBlogs.length} {filteredBlogs.length === 1 ? "guide" : "guides"}
          </span>
        </div>

        {currentGridBlogs.length === 0 ? (
          <div className="qr-empty-box">
            <Icon icon={searchIcon} className="qr-empty-icon" />
            <h3 className="qr-empty-title">No guides match your search</h3>
            <p className="qr-empty-sub">Try another keyword or choose a different category.</p>
            <button
              type="button"
              className="qr-empty-reset"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("All");
                setCurrentPage(1);
              }}
            >
              Reset filters
            </button>
          </div>
        ) : (
          <div className="qr-articles-grid">
            {currentGridBlogs.map((blog) => (
              <a
                key={blog.route}
                className="qr-article-card"
                href={blog.route}
                onClick={(event) => handleCardClick(event, blog)}
              >
                <div className="qr-card-media">
                  {renderCover(blog)}
                  <div className="qr-card-badge-box">
                    <span className="qr-card-badge">{blog.category}</span>
                  </div>
                </div>
                <div className="qr-card-body">
                  <div className="qr-card-date">
                    <Icon icon={calendarIcon} style={{ fontSize: "14px" }} />
                    <span>{blog.date}</span>
                  </div>
                  <h3 className="qr-card-title">{blog.title}</h3>
                  <p className="qr-card-excerpt">{blog.content}</p>
                  <div className="qr-card-footer">
                    <span className="qr-card-cta">
                      <span>Read guide</span>
                      <Icon icon={arrowRightIcon} />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="qr-pagination" aria-label="Guide pagination">
            <button
              type="button"
              className="qr-page-btn"
              disabled={safePage === 1}
              onClick={() => handlePageChange(safePage - 1)}
              title="Previous page"
              aria-label="Previous page"
            >
              <Icon icon={chevronLeft} style={{ fontSize: "20px" }} />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                type="button"
                key={page}
                className={`qr-page-btn ${safePage === page ? "active" : ""}`}
                aria-current={safePage === page ? "page" : undefined}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="qr-page-btn"
              disabled={safePage === totalPages}
              onClick={() => handlePageChange(safePage + 1)}
              title="Next page"
              aria-label="Next page"
            >
              <Icon icon={chevronRight} style={{ fontSize: "20px" }} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default BlogCards;
